import axios from 'axios';
import { getConfig } from '../config/environment';

// Use runtime environment detection to switch between dev and prod APIs
const config = getConfig();
const API_BASE_URL = config.apiUrl;

interface ChatMessage {
  content: string;
  conversation_history?: Array<{
    role: string;
    content: string;
    timestamp: string;
  }>;
  metadata?: Record<string, any>;
}

interface AgentSource {
  title?: string;
  url?: string;
  snippet?: string;
  score?: number;
}

interface AgentResponse {
  content: string;
  session_id: string;
  sources?: AgentSource[];
  confidence?: number;
  requires_human_escalation?: boolean;
  action_required?: string;
  action_data?: any;
  ticket_preview?: any;
}

interface ChatResponse {
  content: string;
  sources: Array<{
    document_id: string;
    title: string;
    content_snippet: string;
    relevance_score: number;
  }>;
  confidence: number;
  suggested_actions: Array<{
    action_type: string;
    description: string;
    parameters?: Record<string, any>;
  }>;
  requires_human_escalation: boolean;
  timestamp: string;
  action_required?: string;
  action_data?: any;
  ticket_preview?: any;
}

class ChatService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle errors
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async sendMessage(message: ChatMessage, user?: any): Promise<ChatResponse> {
    try {
      // Get org slug from localStorage
      const orgSlug = localStorage.getItem('org_slug');
      if (!orgSlug) {
        throw new Error('Organization slug not found. Please log in again.');
      }
      
      const path = `/api/v1/o/${orgSlug}/chat/agent`;
      
      try {
        const response = await this.apiClient.post<AgentResponse>(path, {
          message: message.content,
          session_id: message.metadata?.session_id
        });
        
        // Convert agent response to chat response format
        
        // Map sources
        let sources = (response.data.sources || []).map((source: AgentSource) => ({
          document_id: source.url || '',
          title: source.title || 'Document',
          content_snippet: source.snippet || '',
          relevance_score: source.score ?? 0.5,
          url: source.url
        }));
        
        return {
          content: response.data.content,
          sources,
          confidence: response.data.confidence ?? 0.8,
          suggested_actions: [],
          requires_human_escalation: response.data.requires_human_escalation || false,
          timestamp: new Date().toISOString(),
          action_required: response.data.action_required,
          action_data: response.data.action_data,
          ticket_preview: response.data.ticket_preview
        };
      } catch (innerError: any) {
        console.error('❌ Error inside sendMessage request:', innerError);
        console.error('❌ Error response data:', innerError.response?.data);
        console.error('❌ Error status:', innerError.response?.status);
        throw innerError;
      }
    } catch (error: any) {
      console.error('🔥 Fatal error in sendMessage:', error);
      throw new Error(error.response?.data?.detail || error.response?.data || error.message || 'Failed to send message');
    }
  }

  async streamMessage(
    message: ChatMessage,
    onChunk: (content: string) => void,
    onSource: (source: any) => void,
    onError: (error: string) => void,
    onComplete: () => void
  ): Promise<void> {
    try {
      const orgSlug = localStorage.getItem('org_slug');
      if (!orgSlug) throw new Error('Organization slug not found');

      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('No auth token found');

      const url = `${API_BASE_URL}/api/v1/o/${orgSlug}/chat/stream`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: message.content,
          session_id: message.metadata?.session_id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Stream error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is null');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            if (data.type === 'content') {
              onChunk(data.delta);
            } else if (data.type === 'citation') {
              onSource(data.citation);
            } else if (data.type === 'error') {
              console.error('Chat error:', data.content);
              onError(data.content);
            }
          } catch (e) {
            console.error('❌ Error parsing JSON line:', line, e);
          }
        }
      }
      onComplete();

    } catch (error: any) {
      console.error('Stream error:', error);
      onError(error.message || 'Stream connection failed');
    }
  }

  async getAnalytics(tenantId: string): Promise<any> {
    try {
      const response = await this.apiClient.get(`/api/v1/tenants/${tenantId}/analytics`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get analytics');
    }
  }

  async uploadDocument(tenantId: string, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.apiClient.post(
        `/api/v1/tenants/${tenantId}/knowledge-base/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to upload document');
    }
  }

  async createTicket(issueDescription: string, userEmail: string): Promise<any> {
    try {
      const response = await this.apiClient.post('/api/v1/tickets', {
        issue_description: issueDescription,
        user_email: userEmail,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create ticket');
    }
  }

  async getHealthStatus(): Promise<any> {
    try {
      const response = await this.apiClient.get('/health');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to get health status');
    }
  }

  async downloadDocument(url: string): Promise<string> {
    try {
      const response = await this.apiClient.get(`/documents/download`, {
        params: { url }
      });
      return response.data.download_url;
    } catch (error: any) {
      console.error('Download error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to download document');
    }
  }

  /**
   * Filter sources based on user's document permissions
   * Backend already handles folder-level permissions via document_permissions_service
   * Frontend just passes through - no hardcoded restrictions
   */
  private filterSourcesByPermissions(sources: any[], user: any): any[] {
    // Backend handles all permission filtering via document_permissions_service
    // Frontend should not duplicate this logic or add hardcoded restrictions
    // Folder permissions are configured in admin console and enforced by backend
    return sources;
  }
}

export const chatService = new ChatService();
